import * as React from 'react'
import { getErrorDescription } from '@/lib/error-utils'
import { useForm } from '@tanstack/react-form'
import { create } from '@bufbuild/protobuf'
import { toast } from 'sonner'
import { captureError } from '@/lib/sentry'
import { reportError } from '@/lib/error-reporting'
import {
  Banknote,
  CreditCard,
  Building2,
  Wallet,
  CheckCircle,
  Calendar,
  AlertCircle,
} from 'lucide-react'
import { format } from 'date-fns'

import type { PaymentSchedule } from '@/gen/cortado/v1/invoices_pb'
import { RecordManualPaymentRequestSchema } from '@/gen/cortado/v1/invoices_pb'
import { ManualPaymentMethodEnum } from '@/gen/cortado/v1/settings_pb'
import { useRecordManualPayment } from '@/stores/payments.store'
import { formatCurrency, dollarsToCents } from '@/lib/currency-utils'
import { dateToTimestamp, timestampToDateOnly } from '@/lib/date-utils'
import { cn } from '@/lib/utils'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { DatePicker } from '@/components/shared/date-picker'
import { Progress } from '@/components/ui/progress'

interface RecordPaymentDialogProps {
  invoiceId: string
  paymentSchedules: PaymentSchedule[]
  initialScheduleId?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

const paymentMethodOptions = [
  {
    value: ManualPaymentMethodEnum.MANUAL_PAYMENT_METHOD_CHECK,
    label: 'Check',
    icon: Banknote,
    description: 'Paper check payment',
  },
  {
    value: ManualPaymentMethodEnum.MANUAL_PAYMENT_METHOD_WIRE_TRANSFER,
    label: 'Wire Transfer',
    icon: Building2,
    description: 'Bank wire transfer',
  },
  {
    value: ManualPaymentMethodEnum.MANUAL_PAYMENT_METHOD_ACH,
    label: 'ACH',
    icon: CreditCard,
    description: 'Electronic bank transfer',
  },
  {
    value: ManualPaymentMethodEnum.MANUAL_PAYMENT_METHOD_CASH,
    label: 'Cash',
    icon: Wallet,
    description: 'Cash payment',
  },
]

// Helper to get schedule status info.
//
// `remaining` is what the customer still owes on this schedule and is the cap the
// backend enforces for a manual payment: base remaining PLUS unpaid expected tip.
// On explicit-tip-accounting (v1) invoices the customer's tip is seeded onto the
// schedule as `expectedTipCents`; a manual payment for "invoice total + tip" is
// legal server-side (RecordManualPayment widens the cap by the unpaid tip and
// splits base/tip via the waterfall). Legacy v0 schedules carry expectedTipCents
// = 0, so this reduces to the base-only cap for them.
export function getScheduleStatus(schedule: PaymentSchedule) {
  const total = Number(schedule.amountCents)
  const paid = Number(schedule.amountPaidCents)
  const goodwillRefunded = Number(schedule.goodwillRefundedCents || 0)
  const baseRemaining = Math.max(0, total - paid - goodwillRefunded)
  const tipRemaining = Math.max(
    0,
    Number(schedule.expectedTipCents || 0) - Number(schedule.tipPaidCents || 0),
  )
  const remaining = baseRemaining + tipRemaining
  const effectiveTotal = total - goodwillRefunded
  const progress = effectiveTotal > 0 ? (paid / effectiveTotal) * 100 : 0
  const isPaid = remaining <= 0
  const dueDateOnly = timestampToDateOnly(schedule.dueDate)
  const isOverdue = !isPaid && !!dueDateOnly && dueDateOnly < new Date()

  return {
    total,
    paid,
    remaining,
    baseRemaining,
    tipRemaining,
    progress,
    isPaid,
    isOverdue,
    goodwillRefunded,
    effectiveTotal,
  }
}

export function RecordPaymentDialog({
  invoiceId,
  paymentSchedules,
  initialScheduleId,
  open,
  onOpenChange,
  onSuccess,
}: RecordPaymentDialogProps) {
  const { recordPayment, isRecording } = useRecordManualPayment(invoiceId)

  // Filter to only schedules with remaining balance
  const availableSchedules = React.useMemo(
    () => paymentSchedules.filter((s) => getScheduleStatus(s).remaining > 0),
    [paymentSchedules]
  )

  // Find initial schedule or default to first available
  const defaultScheduleId = React.useMemo(() => {
    if (initialScheduleId) {
      const found = availableSchedules.find((s) => s.id === initialScheduleId)
      if (found) return found.id
    }
    return availableSchedules[0]?.id || ''
  }, [initialScheduleId, availableSchedules])

  // Track selected schedule
  const [selectedScheduleId, setSelectedScheduleId] = React.useState<string>(defaultScheduleId)

  // Get currently selected schedule
  const selectedSchedule = React.useMemo(
    () => availableSchedules.find((s) => s.id === selectedScheduleId) || null,
    [availableSchedules, selectedScheduleId]
  )

  // Calculate remaining balance for selected schedule (base + unpaid tip)
  const selectedStatus = selectedSchedule ? getScheduleStatus(selectedSchedule) : null
  const remainingBalance = selectedStatus?.remaining ?? 0
  const selectedTipRemaining = selectedStatus?.tipRemaining ?? 0

  // Idempotency key minted once per form entry (dialog open) — NOT per render or per
  // mutation call — so a retry of the same submission reuses it and the server
  // dedupes, while two intentional same-amount/same-day payments get distinct keys.
  const [idempotencyKey, setIdempotencyKey] = React.useState<string>(() => crypto.randomUUID())

  const form = useForm({
    defaultValues: {
      paymentMethod: ManualPaymentMethodEnum.MANUAL_PAYMENT_METHOD_CHECK,
      amount: remainingBalance / 100,
      paymentDate: new Date(),
      referenceNumber: '',
      notes: '',
      sendReceiptEmail: true,
    },
    onSubmit: async ({ value }) => {
      try {
        if (!selectedSchedule) {
          toast.error('Please select a payment schedule')
          return
        }

        if (value.amount <= 0) {
          toast.error('Amount must be greater than 0')
          return
        }

        const amountCents = dollarsToCents(value.amount)
        if (amountCents > remainingBalance) {
          toast.error(`Amount exceeds remaining balance of ${formatCurrency(remainingBalance)}`)
          return
        }

        const request = create(RecordManualPaymentRequestSchema, {
          invoiceId,
          paymentScheduleId: selectedSchedule.id,
          amountCents: BigInt(amountCents),
          paymentMethod: value.paymentMethod,
          paymentDate: dateToTimestamp(value.paymentDate),
          referenceNumber: value.referenceNumber || undefined,
          notes: value.notes || undefined,
          sendReceiptEmail: value.sendReceiptEmail,
          idempotencyKey,
        })

        await recordPayment(request)

        toast.success('Manual payment recorded')
        onOpenChange(false)
        form.reset()
        onSuccess?.()
      } catch (error) {
        console.error('Failed to record payment:', error)
        reportError(error, { feature: 'payment_record', kind: 'mutation' })
        captureError(error instanceof Error ? error : new Error(String(error)), { context: 'record-payment-dialog' })
        toast.error('Failed to record payment. Please try again.', {
          description: getErrorDescription(error),
        })
      }
    },
  })

  // Reset form when dialog opens or schedule selection changes; each reset starts a
  // fresh form entry, so mint a new idempotency key alongside it.
  React.useEffect(() => {
    if (open) {
      form.reset()
      setSelectedScheduleId(defaultScheduleId)
      setIdempotencyKey(crypto.randomUUID())
    }
  }, [open, defaultScheduleId])

  // Update amount when schedule changes
  React.useEffect(() => {
    if (selectedSchedule) {
      const newRemaining = getScheduleStatus(selectedSchedule).remaining
      form.setFieldValue('amount', newRemaining / 100)
    }
  }, [selectedScheduleId, selectedSchedule])

  // Handle case where no schedules have remaining balance
  const hasNoAvailableSchedules = availableSchedules.length === 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">Record a Manual Payment</DialogTitle>
          <DialogDescription>
            Log a payment received outside of Stripe (e.g. check, cash, wire transfer). This does not process a charge.
          </DialogDescription>
        </DialogHeader>

        {hasNoAvailableSchedules ? (
          <div className="py-8 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <CheckCircle className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="font-medium">All payment schedules are fully paid</p>
            <p className="mt-1 text-sm text-muted-foreground">
              There are no outstanding balances to record a payment against.
            </p>
          </div>
        ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            form.handleSubmit()
          }}
          className="space-y-6"
        >
          {/* Payment Schedule Selector */}
          {availableSchedules.length > 1 ? (
            <div className="space-y-3">
              <Label className="text-base font-semibold">
                Apply payment to <span className="text-destructive">*</span>
              </Label>
              <div className="space-y-2">
                {availableSchedules.map((schedule) => {
                  const status = getScheduleStatus(schedule)
                  const isSelected = selectedScheduleId === schedule.id
                  const dueDate = timestampToDateOnly(schedule.dueDate) ?? null

                  return (
                    <button
                      key={schedule.id}
                      type="button"
                      onClick={() => setSelectedScheduleId(schedule.id)}
                      className={cn(
                        'relative w-full rounded-xl border-2 p-4 text-left transition-all duration-200',
                        'hover:shadow-md hover:border-primary/40',
                        isSelected
                          ? 'border-primary bg-primary/[0.03] shadow-sm ring-1 ring-primary/20'
                          : 'border-border/60 bg-card hover:bg-accent/30'
                      )}
                    >
                      {/* Selection indicator */}
                      <div
                        className={cn(
                          'absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full transition-all',
                          isSelected
                            ? 'bg-primary text-primary-foreground'
                            : 'border-2 border-muted-foreground/30'
                        )}
                      >
                        {isSelected && <CheckCircle className="h-3 w-3" />}
                      </div>

                      {/* Schedule content */}
                      <div className="pr-8">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className={cn(
                              'font-semibold truncate',
                              isSelected && 'text-primary'
                            )}>
                              {schedule.description || 'Payment Schedule'}
                            </p>
                            {dueDate && (
                              <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                                <Calendar className="h-3 w-3" />
                                <span>Due {format(dueDate, 'MMM d, yyyy')}</span>
                                {status.isOverdue && (
                                  <span className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-destructive/10 px-1.5 py-0.5 text-[10px] font-medium text-destructive">
                                    <AlertCircle className="h-2.5 w-2.5" />
                                    Overdue
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="text-right shrink-0">
                            <p className={cn(
                              'font-financial text-lg font-bold',
                              isSelected ? 'text-primary' : 'text-foreground'
                            )}>
                              {formatCurrency(status.remaining)}
                            </p>
                            <p className="font-financial text-xs text-muted-foreground">
                              of {formatCurrency(status.total)}
                              {status.tipRemaining > 0 && ` + ${formatCurrency(status.tipRemaining)} tip`}
                            </p>
                          </div>
                        </div>

                        {/* Progress bar */}
                        <div className="mt-3">
                          <Progress
                            value={status.progress}
                            className="h-1.5"
                          />
                          <p className="mt-1 text-[10px] text-muted-foreground">
                            {Math.round(status.progress)}% paid
                          </p>
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          ) : selectedSchedule && (
            // Single schedule - show simple info card
            <div className="rounded-xl border-2 border-primary/20 bg-gradient-to-br from-primary/[0.03] to-transparent p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-primary">
                    {selectedSchedule.description || 'Payment Schedule'}
                  </p>
                  <p className="text-sm text-muted-foreground">Remaining balance</p>
                </div>
                <div className="text-right">
                  <p className="font-financial text-2xl font-bold text-primary">
                    {formatCurrency(remainingBalance)}
                  </p>
                  {selectedTipRemaining > 0 && (
                    <p
                      className="font-financial text-xs text-muted-foreground"
                      data-testid="record-payment-tip-breakdown"
                    >
                      {formatCurrency(remainingBalance - selectedTipRemaining)} balance +{' '}
                      {formatCurrency(selectedTipRemaining)} tip
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Payment Method Selector */}
          <form.Field name="paymentMethod">
            {(field) => (
              <div className="space-y-3">
                <Label>Payment Method</Label>
                <div className="grid grid-cols-2 gap-2">
                  {paymentMethodOptions.map((option) => {
                    const Icon = option.icon
                    const isSelected = field.state.value === option.value
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => field.handleChange(option.value)}
                        className={cn(
                          'relative flex flex-col items-center gap-2 rounded-lg border-2 p-4 transition-all',
                          'hover:border-primary/50 hover:bg-muted/50',
                          isSelected
                            ? 'border-primary bg-primary/5'
                            : 'border-muted'
                        )}
                      >
                        {isSelected && (
                          <div className="absolute top-2 right-2">
                            <CheckCircle className="h-4 w-4 text-primary" />
                          </div>
                        )}
                        <Icon className={cn(
                          'h-6 w-6',
                          isSelected ? 'text-primary' : 'text-muted-foreground'
                        )} />
                        <div className="text-center">
                          <p className={cn(
                            'font-medium text-sm',
                            isSelected ? 'text-primary' : ''
                          )}>
                            {option.label}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {option.description}
                          </p>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </form.Field>

          {/* Amount with quick-fill buttons */}
          <form.Field name="amount">
            {(field) => (
              <div className="space-y-3">
                <Label htmlFor="amount">
                  Amount <span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">
                    $
                  </span>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={remainingBalance / 100}
                    className="font-financial pl-7 text-lg font-semibold h-12"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(Math.max(0, parseFloat(e.target.value) || 0))}
                  />
                </div>
                {/* Quick fill buttons */}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => field.handleChange(remainingBalance / 100)}
                  >
                    Full Balance
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => field.handleChange(Math.round(remainingBalance / 2) / 100)}
                  >
                    Half
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => field.handleChange(0)}
                  >
                    Custom
                  </Button>
                </div>
                {field.state.value > remainingBalance / 100 && (
                  <p className="text-xs text-destructive">
                    Amount exceeds remaining balance of {formatCurrency(remainingBalance)}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          {/* Payment Date */}
          <form.Field name="paymentDate">
            {(field) => (
              <div className="space-y-2">
                <Label>Payment Date</Label>
                <DatePicker
                  value={field.state.value}
                  onChange={(date) => field.handleChange(date || new Date())}
                />
              </div>
            )}
          </form.Field>

          {/* Reference Number */}
          <form.Field name="referenceNumber">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor="referenceNumber">
                  Reference Number <span className="text-muted-foreground">(Optional)</span>
                </Label>
                <Input
                  id="referenceNumber"
                  placeholder="Check number, transaction ID, etc."
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
              </div>
            )}
          </form.Field>

          {/* Notes */}
          <form.Field name="notes">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor="notes">
                  Notes <span className="text-muted-foreground">(Optional)</span>
                </Label>
                <Textarea
                  id="notes"
                  placeholder="Any additional notes about this payment"
                  rows={2}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
              </div>
            )}
          </form.Field>

          {/* Send Receipt Email Option */}
          <form.Field name="sendReceiptEmail">
            {(field) => (
              <div className="flex items-center space-x-2 pt-4 border-t">
                <Checkbox
                  id="sendReceiptEmail"
                  checked={field.state.value}
                  onCheckedChange={(checked) => field.handleChange(!!checked)}
                />
                <Label htmlFor="sendReceiptEmail" className="cursor-pointer">
                  <span className="font-normal">Send receipt email to customer</span>
                </Label>
              </div>
            )}
          </form.Field>

          <DialogFooter className="sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isRecording}
            >
              Cancel
            </Button>
            <form.Subscribe selector={(s) => [s.values.amount, s.isSubmitting] as const}>
              {([amount, isSubmitting]) => (
                <Button
                  type="submit"
                  disabled={isRecording || isSubmitting || !selectedSchedule || amount <= 0}
                >
                  {isRecording ? 'Recording...' : 'Record Payment'}
                </Button>
              )}
            </form.Subscribe>
          </DialogFooter>
        </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
